import { z } from "zod";

import { withZod } from "@rvf/zod";

export const RESOURCE_URL = "/resources/save-for-later";
export const GUEST_SAVE_FOR_LATER_KEY = "guest_save_for_later";

export const GuestSaveForLaterSchema = z.object({
  items: z.array(z.object({ productId: z.string().min(1) })),
});
export type GuestSaveForLater = z.infer<typeof GuestSaveForLaterSchema>;

export const SaveForLaterSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("add"), productId: z.string().min(2) }),
  z.object({ action: z.literal("remove"), productId: z.string().min(2) }),
  /**
   * Atomic cart-line → SFL move. Server adds the product to SFL and removes
   * the basket line item in a single round-trip so a partial failure can't
   * leave the UI in an inconsistent state.
   */
  z.object({
    action: z.literal("saveLineItem"),
    productId: z.string().min(2),
    itemId: z.string().min(1),
  }),
]);

export type Actions = z.infer<typeof SaveForLaterSchema>;

export const SaveForLaterSchemaValidator = withZod(SaveForLaterSchema);

const SflAddResultSchema = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true), productIds: z.array(z.string()) }),
  z.object({
    ok: z.literal(false),
    reason: z.literal("DUPLICATE"),
    productIds: z.array(z.string()),
  }),
]);

/**
 * Shape of the `/resources/save-for-later` action's success responses.
 * `validationError(...)` responses are filtered out by `.safeParse(...)`
 * failing — consumers don't need a separate guard.
 */
export const SflActionResponseSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("added"), result: SflAddResultSchema }),
  z.object({ kind: z.literal("removed"), productIds: z.array(z.string()) }),
  z.object({
    kind: z.literal("savedLineItem"),
    result: SflAddResultSchema,
    partial: z.boolean().optional(),
  }),
  /**
   * COM-63 (P1-1) — `clientAction` containment payload for a network-level
   * submission failure (the server `action`'s catch never sees those). Echoes
   * the attempted action + productId so settle observers can roll back
   * optimistic UI for the right row. Consumers that only match a success
   * `kind` fall through to their failure branch on this, same as they do for
   * a `validationError` payload.
   */
  z.object({
    kind: z.literal("sfl-error"),
    action: z.enum(["add", "remove", "saveLineItem"]),
    productId: z.string(),
  }),
]);
export type SflActionResponse = z.infer<typeof SflActionResponseSchema>;
